/* eslint complexity: ["error", 11] */
import { createContext, JSX, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useBrand } from '@cof/plastic-components';
import { jwtDecode } from 'jwt-decode';
import merge from 'lodash.merge';

import { useFormData as useFormDataFetch } from '../../data';
import { BTVendorCodes, CONSUMER_NAMES } from '../../utilities/constants';
import pushToDataLayer from '../../utilities/dataLayer/dataLayer';
import useQueryParams from '../../utilities/useQueryParams';
import useTechnicalError from '../../utilities/useTechnicalError';
import { getKey } from '../../utilities/formValuesMutation/formValuesMutation';
import partners from '../../partners';
import { FormDataContextValue, FormDataJwtPayload } from '../../types';

export const FormDataContext = createContext<FormDataContextValue>({ isEdit: false, isBT: false, formLoading: false });

export const resolveVendorCode = (
    parsedFormData: FormDataJwtPayload | undefined,
    urlVendorCode: string | undefined,
    oemc: string | undefined
): string => parsedFormData?.vendorCode || urlVendorCode || oemc || 'CQC';

const FormDataContextProvider = ({ children }: PropsWithChildren): JSX.Element => {
    const [parsedFormData, setParsedFormData] = useState<FormDataJwtPayload>();
    const queryParams = useQueryParams();
    const [initialQueryParams] = useState(queryParams);
    const brand = useBrand();
    const consumerName = CONSUMER_NAMES[brand];
    const {
        oemc,
        vendorCode: urlVendorCode,
        sParameter,
        quotation,
        productId,
        pcr,
        upstreamTarget
    } = initialQueryParams;

    const [execFormCall, formLoading, { error: formDataError, data: formDataResponse }] = useFormDataFetch(
        {
            // keys must be in alphabetical order unless query param ordering middleware is added to Tyk API definition
            consumerName,
            oemc,
            sParameter,
            vc: urlVendorCode,
            quotation,
            upstreamTarget
        },
        parsedFormData
    );

    useEffect(() => {
        if (!['/ob-pending', '/ob-landing'].includes(window.location.pathname)) {
            execFormCall();
        }
    }, []);

    useTechnicalError(!quotation ? null : formDataError);

    const mergeResponseData = useCallback(
        (decodedFormDataResponse: FormDataJwtPayload) => {
            const formDataConsumerName = decodedFormDataResponse?.consumerName;
            const formDataOemc = decodedFormDataResponse?.onlineEntryMethodCode;

            merge(decodedFormDataResponse, {
                consumerName: formDataConsumerName || consumerName,
                vendorCode: urlVendorCode || formDataOemc || oemc || 'CQC',
                oemc
            });
        },
        [consumerName, oemc, urlVendorCode]
    );

    useEffect(() => {
        if (!formDataResponse) return;
        // parse the form data
        const { formResponse } = formDataResponse;
        const formDataResponseDecoded = jwtDecode<FormDataJwtPayload>(formResponse);

        const { formValues } = formDataResponseDecoded;

        mergeResponseData(formDataResponseDecoded);

        // all information has been injected at this point
        setParsedFormData(formDataResponseDecoded);

        pushToDataLayer({
            ccsNumber: productId,
            vendorCode: formDataResponseDecoded.vendorCode,
            consumerName: formDataResponseDecoded.consumerName,
            definitionId: formDataResponseDecoded?.definition?.id,
            journeyType: formValues && Object.entries(formValues).length > 0 ? 'pre-pop' : 'standard',
            pcr
        });
    }, [formDataResponse, consumerName, oemc, pcr, productId, urlVendorCode, mergeResponseData, quotation, sParameter]);

    const isLoading = formLoading || parsedFormData == null;
    return (
        <FormDataContext.Provider
            value={{
                formData: parsedFormData,
                formResponse: formDataResponse?.formResponse,
                formLoading: isLoading,
                quotation,
                isEdit: !!quotation,
                vendorCode: resolveVendorCode(parsedFormData, urlVendorCode, oemc),
                isBT:
                    (oemc !== undefined && BTVendorCodes.includes(oemc) && brand !== partners.ASOS) || // ASOS is the only hosted partner that does not offer BTs
                    !!(parsedFormData?.formValues && !!getKey('balance-transfer', parsedFormData.formValues)),
                consumerName: parsedFormData?.consumerName || consumerName, // We should always use the consumer name from the initial quote if it exists
                xUpstreamEnv: upstreamTarget
            }}
        >
            {children}
        </FormDataContext.Provider>
    );
};
export const useFormData = (): FormDataContextValue => useContext(FormDataContext);

export default FormDataContextProvider;

FormDataContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};
